import { Typography, Box } from '@mui/material';
import { netsuiteIntegrationSelectors } from '@vy/app-selectors';
import type { FC } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { RadioGroup } from '../../../../components/RadioGroup';
import { ConfigurationSection } from '../../../../containers/ConfigurationSection';
import type {
  NetSuiteConfigurationControl,
  NetSuiteConfigurationFormValues,
} from '../../NetSuiteConfigurationFormSchema';
import { TAX_EXPORT_STRATEGY } from '@vy/integrations-types';
import { TAX_EXPORT_STRATEGY_OPTIONS } from './constants';
import { ControlledNetSuiteProductDropdown } from './ControlledNetSuiteProductDropdown';
import { useSubsidiaries } from '../../../../../../../../hooks/requests/use-subsidiaries';

type TaxConfigurationSectionProps = {
  control: NetSuiteConfigurationControl;
  defaultValues: NetSuiteConfigurationFormValues;
};

export const TaxConfigurationSection: FC<TaxConfigurationSectionProps> = ({
  control,
  defaultValues,
}) => {
  const taxExportStrategy = useWatch({ control, name: 'taxExportStrategy' });
  const { data: subsidiariesResponse } = useSubsidiaries();
  const isSeparateLineItem = taxExportStrategy === TAX_EXPORT_STRATEGY.SEPARATE_LINE_ITEM;
  const subsidiaries = subsidiariesResponse?.subsidiaries ?? [];

  return (
    <ConfigurationSection title="Tax" iconName="InvoiceListItem">
      <Controller
        name="taxExportStrategy"
        control={control}
        defaultValue={defaultValues.taxExportStrategy}
        render={({ field: { value, onChange } }) => (
          <div data-vy={netsuiteIntegrationSelectors.configuration.taxStrategyRadio}>
            <RadioGroup
              title="Choose how tax is exported"
              value={value}
              onChange={onChange}
              keyExtractor={(option) => option.value}
              options={TAX_EXPORT_STRATEGY_OPTIONS}
            />
          </div>
        )}
      />
      {isSeparateLineItem && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography variant="subtitle2">
            Select a NetSuite product for tax
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '12px',
              }}
            >
              Account
            </Typography>
            <ControlledNetSuiteProductDropdown
              control={control}
              fieldPath="accountTaxItem"
              showDropdown={true}
              placeholder="Select NetSuite tax item"
            />
          </Box>
          {subsidiaries.map((subsidiary) => (
            <Box key={subsidiary.id} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '12px',
                }}
              >
                {subsidiary.name}
              </Typography>
              <ControlledNetSuiteProductDropdown
                control={control}
                fieldPath={`taxItemBySubsidiaryId.${subsidiary.id}`}
                showDropdown={true}
                placeholder="Select NetSuite tax item"
              />
            </Box>
          ))}
        </Box>
      )}
    </ConfigurationSection>
  );
};
